import { createTheme, darken, lighten, Theme } from '@mui/material'
import { ThemeMode } from '../context/theme_mode_context'

interface CreateAppThemeProps {
    primaryColor?: string
    hoverColor?: string
    mode?: ThemeMode
}

const FONT_FAMILY =
    "'Poppins', 'Inter', 'Segoe UI', Roboto, -apple-system, sans-serif"

export const createAppTheme = ({
    primaryColor,
    hoverColor,
    mode = 'light',
}: CreateAppThemeProps): Theme => {
    const isDark = mode === 'dark'
    const primary = primaryColor || (isDark ? '#4F46E5' : '#3a49e7')
    const safeLighten = (color: string, amount: number) => {
        try {
            return lighten(color, amount)
        } catch {
            return color
        }
    }
    const safeDarken = (color: string, amount: number) => {
        try {
            return darken(color, amount)
        } catch {
            return color
        }
    }
    const hover =
        hoverColor ||
        (() => {
            try {
                return darken(primary, 0.12)
            } catch {
                return isDark ? '#4338CA' : '#2E3BBD'
            }
        })()

    return createTheme({
        palette: {
            mode,
            primary: {
                main: primary,
                light: safeLighten(primary, 0.35),
                dark: safeDarken(primary, 0.15),
                contrastText: '#FFFFFF',
            },
            secondary: {
                main: '#FFFFFF',
                contrastText: isDark ? '#E6E9EF' : '#1A2233',
            },
            background: {
                default: isDark ? '#0F1117' : '#F3F4F9',
                paper: isDark ? '#1A1F2A' : '#FFFFFF',
            },
            text: {
                primary: isDark ? '#E6E9EF' : '#1A2233',
                secondary: isDark ? '#9AA3B2' : '#6B7280',
                disabled: isDark ? '#6B7280' : '#9CA3AF',
            },
            divider: isDark ? '#2B3242' : '#E7EAF0',
            error: {
                main: isDark ? '#EF4444' : '#DC2626',
                light: isDark ? '#7F1D1D' : '#FECACA',
                dark: isDark ? '#DC2626' : '#B91C1C',
                contrastText: '#FFFFFF',
            },
            warning: {
                main: isDark ? '#F59E0B' : '#D97706',
                light: isDark ? '#78350F' : '#FDE68A',
                dark: isDark ? '#D97706' : '#B45309',
                contrastText: '#FFFFFF',
            },
            info: {
                main: isDark ? '#3B82F6' : '#2563EB',
                light: isDark ? '#1E3A8A' : '#BFDBFE',
                dark: isDark ? '#2563EB' : '#1D4ED8',
                contrastText: '#FFFFFF',
            },
            success: {
                main: isDark ? '#22C55E' : '#16A34A',
                light: isDark ? '#14532D' : '#BBF7D0',
                dark: isDark ? '#16A34A' : '#15803D',
                contrastText: '#FFFFFF',
            },
        },
        shape: {
            borderRadius: 10,
        },
        typography: {
            fontFamily: FONT_FAMILY,
            fontSize: 14,
            h1: { fontWeight: 700 },
            h2: { fontWeight: 700 },
            h3: { fontWeight: 700 },
            h4: { fontWeight: 700 },
            h5: { fontWeight: 700 },
            h6: { fontWeight: 600 },
            subtitle1: { fontWeight: 600 },
            subtitle2: { fontWeight: 600 },
            body1: { fontSize: '0.875rem', lineHeight: 1.5 },
            body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
            button: { textTransform: 'none', fontWeight: 600 },
        },
        shadows: [
            'none',
            '0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.1)',
            '0 1px 3px rgba(16, 24, 40, 0.08), 0 4px 12px rgba(16, 24, 40, 0.08)',
            '0 4px 12px rgba(16, 24, 40, 0.1), 0 8px 24px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 12px 32px rgba(16, 24, 40, 0.14), 0 20px 40px rgba(16, 24, 40, 0.1)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
        ],
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: 'var(--app-bg)',
                        WebkitFontSmoothing: 'antialiased',
                        MozOsxFontSmoothing: 'grayscale',
                    },
                },
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true,
                },
                styleOverrides: {
                    root: {
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        transition:
                            'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                    sizeSmall: { padding: '6px 14px', fontSize: '0.8125rem' },
                    sizeMedium: { padding: '9px 18px', fontSize: '0.875rem' },
                    sizeLarge: { padding: '12px 24px', fontSize: '0.9375rem' },
                    containedPrimary: {
                        color: '#FFFFFF',
                        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.28)',
                        '&:hover': {
                            backgroundColor: hover,
                            boxShadow: '0 6px 16px rgba(79, 70, 229, 0.32)',
                        },
                    },
                    containedSecondary: {
                        color: isDark ? '#E6E9EF' : '#1A2233',
                        backgroundColor: 'var(--surface)',
                        border: '1px solid var(--border-color)',
                        '&:hover': {
                            backgroundColor: isDark
                                ? 'rgba(255, 255, 255, 0.06)'
                                : '#F5F6F8',
                            borderColor: isDark
                                ? 'rgba(255, 255, 255, 0.2)'
                                : '#C2C8D6',
                        },
                    },
                    containedSuccess: {
                        boxShadow: '0 4px 12px rgba(22, 163, 74, 0.28)',
                    },
                    containedError: {
                        boxShadow: '0 4px 12px rgba(220, 38, 38, 0.28)',
                    },
                    containedWarning: {
                        boxShadow: '0 4px 12px rgba(217, 119, 6, 0.28)',
                    },
                    outlined: {
                        borderColor: '#C2C8D6',
                        color: 'var(--text-primary)',
                        '&:hover': {
                            backgroundColor: isDark
                                ? 'rgba(255, 255, 255, 0.06)'
                                : '#F7F8FB',
                            borderColor: isDark
                                ? 'rgba(255, 255, 255, 0.25)'
                                : '#A9B0BE',
                        },
                    },
                    outlinedPrimary: {
                        borderColor: 'rgba(79, 70, 229, 0.5)',
                        '&:hover': {
                            backgroundColor: 'rgba(79, 70, 229, 0.08)',
                            borderColor: primary,
                        },
                    },
                    text: {
                        '&:hover': {
                            backgroundColor: isDark
                                ? 'rgba(255, 255, 255, 0.06)'
                                : 'rgba(16, 24, 40, 0.04)',
                        },
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '10px',
                        transition:
                            'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                    colorPrimary: { color: primary },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: 'var(--text-secondary)',
                        '&.Mui-focused': {
                            color: primary,
                            fontWeight: 600,
                        },
                        '&.Mui-error': {
                            color: '#EF4444',
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '10px',
                        backgroundColor: 'var(--surface)',
                        transition:
                            'border-color 150ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--border-color)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#A9B0BE',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: primary,
                            borderWidth: '2px',
                        },
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#EF4444',
                        },
                        '&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline':
                            {
                                borderWidth: '2px',
                            },
                    },
                    input: {
                        '&::placeholder': {
                            color: '#9CA3AF',
                            opacity: 1,
                        },
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontSize: '0.875rem',
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    select: {
                        borderRadius: '10px',
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        fontSize: '0.875rem',
                        borderRadius: '8px',
                        margin: '2px 6px',
                        padding: '8px 12px',
                        minHeight: 'auto',
                        '&.Mui-selected': {
                            backgroundColor: 'rgba(79, 70, 229, 0.12)',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: 'rgba(79, 70, 229, 0.16)',
                            },
                        },
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        borderRadius: '12px',
                        boxShadow:
                            '0 8px 24px rgba(16, 24, 40, 0.12), 0 16px 32px rgba(16, 24, 40, 0.08)',
                        border: '1px solid var(--border-color)',
                        padding: '4px',
                        backgroundImage: 'none',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                    rounded: {
                        borderRadius: '12px',
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: '14px',
                        border: '1px solid var(--border-color)',
                        boxShadow:
                            '0 1px 3px rgba(16, 24, 40, 0.06), 0 4px 12px rgba(16, 24, 40, 0.04)',
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: '16px',
                        boxShadow:
                            '0 12px 32px rgba(16, 24, 40, 0.16), 0 24px 48px rgba(16, 24, 40, 0.1)',
                        backgroundImage: 'none',
                    },
                    paperFullScreen: {
                        borderRadius: 0,
                    },
                },
            },
            MuiBackdrop: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'rgba(15, 23, 42, 0.45)',
                        backdropFilter: 'blur(2px)',
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        padding: '18px 24px',
                    },
                },
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        padding: '8px 24px 20px',
                        color: 'var(--text-primary)',
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        padding: '14px 24px 18px',
                    },
                },
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        borderColor: 'var(--border-color)',
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        fontWeight: 500,
                        fontSize: '0.8125rem',
                    },
                    filled: {
                        backgroundColor: '#EFF1F6',
                        color: 'var(--text-primary)',
                    },
                    colorPrimary: {
                        backgroundColor: 'rgba(79, 70, 229, 0.12)',
                        color: primary,
                    },
                    colorSecondary: {
                        backgroundColor: isDark
                            ? 'rgba(255, 255, 255, 0.08)'
                            : '#EFF1F6',
                        color: 'var(--text-primary)',
                    },
                    outlined: {
                        borderColor: 'var(--border-color)',
                        color: 'var(--text-primary)',
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: isDark ? '#3A4356' : '#1A2233',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        padding: '6px 10px',
                        boxShadow: '0 4px 12px rgba(16, 24, 40, 0.2)',
                    },
                    arrow: {
                        color: isDark ? '#3A4356' : '#1A2233',
                    },
                },
            },
            MuiTableContainer: {
                styleOverrides: {
                    root: {
                        borderRadius: '12px',
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderBottom: '1px solid var(--border-soft)',
                        padding: '12px 16px',
                        color: 'var(--text-primary)',
                    },
                    head: {
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        backgroundColor: 'var(--surface-muted)',
                        whiteSpace: 'nowrap',
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        transition: 'background-color 120ms ease',
                        '&:hover': {
                            backgroundColor: 'rgba(79, 70, 229, 0.04)',
                        },
                    },
                    head: {
                        '&:hover': {
                            backgroundColor: 'var(--surface-muted)',
                        },
                    },
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        borderRadius: '6px',
                        padding: 8,
                    },
                    colorPrimary: {
                        color: '#C2C8D6',
                        '&.Mui-checked': {
                            color: primary,
                        },
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: {
                        padding: 8,
                    },
                    colorPrimary: {
                        color: '#C2C8D6',
                        '&.Mui-checked': {
                            color: primary,
                        },
                    },
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    root: {
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            color: primary,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                            {
                                backgroundColor: primary,
                                opacity: 0.5,
                            },
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        borderRadius: '4px 4px 0 0',
                        height: 3,
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                    },
                },
            },
            MuiAvatar: {
                styleOverrides: {
                    root: {
                        fontWeight: 600,
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    colorSecondary: {
                        backgroundColor: 'var(--surface)',
                        color: isDark ? '#E6E9EF' : '#1A2233',
                    },
                    colorPrimary: {
                        backgroundColor: primary,
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        border: 'none',
                    },
                },
            },
            MuiListItem: {
                styleOverrides: {
                    root: {
                        borderRadius: '10px',
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '10px',
                        transition:
                            'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1), color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                },
            },
            MuiListItemIcon: {
                styleOverrides: {
                    root: {
                        minWidth: 38,
                    },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)',
                        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.04)',
                        overflow: 'hidden',
                        marginBottom: '8px',
                        '&:before': {
                            display: 'none',
                        },
                        '&.Mui-expanded': {
                            margin: '0 0 8px',
                        },
                    },
                },
            },
            MuiAccordionSummary: {
                styleOverrides: {
                    root: {
                        borderRadius: '12px',
                        minHeight: 48,
                        '&.Mui-expanded': {
                            minHeight: 48,
                        },
                    },
                },
            },
            MuiAccordionDetails: {
                styleOverrides: {
                    root: {
                        padding: '16px',
                        backgroundColor: 'var(--surface-soft)',
                    },
                },
            },
            MuiBreadcrumbs: {
                styleOverrides: {
                    root: {
                        fontSize: '0.8125rem',
                        color: '#6B7280',
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: '10px',
                        fontWeight: 500,
                    },
                    standardSuccess: {
                        backgroundColor: isDark ? '#14532D' : '#DCFCE7',
                        color: isDark ? '#BBF7D0' : '#166534',
                    },
                    standardError: {
                        backgroundColor: isDark ? '#7F1D1D' : '#FEE2E2',
                        color: isDark ? '#FECACA' : '#991B1B',
                    },
                    standardWarning: {
                        backgroundColor: isDark ? '#78350F' : '#FEF3C7',
                        color: isDark ? '#FDE68A' : '#92400E',
                    },
                    standardInfo: {
                        backgroundColor: isDark ? '#1E3A8A' : '#DBEAFE',
                        color: isDark ? '#BFDBFE' : '#1E40AF',
                    },
                    filledSuccess: {
                        backgroundColor: isDark ? '#22C55E' : '#16A34A',
                    },
                    filledError: {
                        backgroundColor: isDark ? '#EF4444' : '#DC2626',
                    },
                    filledWarning: {
                        backgroundColor: isDark ? '#F59E0B' : '#D97706',
                    },
                    filledInfo: {
                        backgroundColor: isDark ? '#3B82F6' : '#2563EB',
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        borderRadius: '999px',
                        backgroundColor: 'var(--border-color)',
                    },
                    bar: {
                        borderRadius: '999px',
                    },
                },
            },
            MuiCircularProgress: {
                styleOverrides: {
                    colorPrimary: {
                        color: primary,
                    },
                },
            },
            MuiPaginationItem: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        fontWeight: 500,
                    },
                    selected: {
                        '&.Mui-selected': {
                            backgroundColor: primary,
                            color: '#FFFFFF',
                            '&:hover': {
                                backgroundColor: hover,
                            },
                        },
                    },
                },
            },
            MuiBadge: {
                styleOverrides: {
                    badge: {
                        fontWeight: 700,
                    },
                },
            },
            MuiSkeleton: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        backgroundColor: 'var(--border-color)',
                    },
                },
            },
            MuiStepLabel: {
                styleOverrides: {
                    label: {
                        fontWeight: 500,
                        fontSize: '0.8125rem',
                        '&.Mui-active': {
                            fontWeight: 600,
                        },
                    },
                },
            },
            MuiStepIcon: {
                styleOverrides: {
                    root: {
                        color: 'var(--border-color)',
                        '&.Mui-active': {
                            color: primary,
                        },
                        '&.Mui-completed': {
                            color: '#22C55E',
                        },
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& label.Mui-focused': {
                            color: primary,
                        },
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                        },
                    },
                },
            },
        },
    })
}

export const lightTheme = createAppTheme({ mode: 'light' })

export default createAppTheme
